import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';
//const compareObjects = require('fast-deep-equal');

import { IGlob } from '../ts/Glob';
import WidgetParametersEdit from './WidgetParametersEdit';
import { WidgetTypeSelect } from './WidgetTypeSelect';
import { PasteButton } from '../../../_common/platform/tsx/PasteTargetDialog';
import { EditTargetDialog } from '../../../_common/platform/tsx/EditTargetDialog';
import { WidgetData } from '../../../_common/platform/ts/WidgetData';

import ClipboardCopy from './ClipboardCopy';
import * as Utilities from '../../../_common/platform/ts/Utilities';
import { dropBrokenValidators, IValuesCollection } from '../../../_common/platform/ts/appTheme';
import { ThemeFieldComponent } from '../../../_common/platform/tsx/ThemeField';
import { config } from '../ts/config';
import { getStyles, useResizeAndMoveHook } from '../../../_common/platform/tsx/mixins/ResizeMove';
import { useOverflowHook } from '../../../_common/platform/tsx/mixins/OverflowMixin';
import { IDataEditValues } from '../../../_common/platform/tsx/dataEdit/Interfaces';
import { IDrillDownWidgetParameters, IWidget } from '../ts/Interfaces';

export function WidgetEdit(props: { theme: IValuesCollection; glob: IGlob }) {
    const storeState = props.glob.WidgetEditStore.useSimpleStoreStateHook();
    const datasourcesLoaded = props.glob.EntityModelsStore.useLoadDataSourcesHook();
    const initialData = React.useMemo<string>(() => {
        if (storeState.widgetDataEdit != null) {
            return JSON.stringify(storeState.widgetDataEdit.getDataForSave());
        }

        return null;
    }, []);
    const addColDef = React.useRef<JQueryDeferred<any>>(null);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);

    const { widgetDataEdit } = storeState;
    const dataEditWidget = widgetDataEdit.widget;
    const dialogName = (widgetDataEdit.id || widgetDataEdit.cid).toString();

    const defaultWidgetEditSize = dataEditWidget?.paramsLayout?.defaultWidgetEditSize;
    const defaultContainerHeight = defaultWidgetEditSize?.height || 'max';
    const defaultContentWidth = defaultWidgetEditSize?.width || '3/4';

    const { containerSize, initialContainerHeight, initialContentWidth, position } = useResizeAndMoveHook(
        {
            dialogName: dialogName,
            draggable: true,
            resizable: true
        },
        defaultContainerHeight,
        defaultContentWidth,
        contentRef,
        headerRef
    );
    const containerHeight = useOverflowHook(false, containerSize.height, containerRef, headerRef);

    const displayErrors = () => {
        if (dataEditWidget == null || !dataEditWidget.isInvalid) {
            return;
        }

        const params = dataEditWidget.parameters;
        const errors = _.reduce(
            _.keys(params),
            (res, key) => {
                return res.concat(params[key].errors);
            },
            []
        );

        if (errors.length === 0) {
            return;
        }

        props.glob.DialogActions.createDialog(
            'Configuration is not valid',
            '',
            () => {},
            () => {},
            () => {},
            'confirm',
            {
                content: (
                    <div>
                        <strong>{'This widget has following errors:'}</strong>
                        <ul className="list-unstyled">
                            {errors.map((str, i) => {
                                return <li key={i}>– {str}</li>;
                            })}
                        </ul>
                    </div>
                ),
                confirmTitle: null,
                cancelTitle: null,
                closeTitle: 'OK'
            }
        );
    };

    const closeDialog = () => {
        if (widgetDataEdit != null) {
            ThemeFieldComponent.clearThemeFieldState(widgetDataEdit.id);
            //LL: for WidgetContainers:
            if (widgetDataEdit.values != null) {
                const widgetData = widgetDataEdit.values[widgetDataEdit.type];
                // pdc-widget-layout (nested), pdc-widget-multiple (tabbed), pdc-widget-drill-down
                if (widgetData != null) {
                    const drillDownWidgetParameters = widgetData as IDrillDownWidgetParameters;

                    let widgets: IWidget[];

                    if (drillDownWidgetParameters.drillDown != null) {
                        widgets = drillDownWidgetParameters.drillDown.widgets;
                    } else if (widgetData.widgets != null) {
                        widgets = widgetData.widgets; // pdc-widget-layout (nested), pdc-widget-multiple (tabbed)
                    }

                    if (widgets != null && widgets.length > 0) {
                        widgets.forEach((widget) => {
                            ThemeFieldComponent.clearThemeFieldState(widget.id);
                        });
                    }
                }
            }
        }

        props.glob.AppActions.widgetEditHide();
    };

    const createWidget = (pos) => {
        const { widgetEditWorkspace, widgetParentWidget, widgetEditPos } = storeState;

        if (widgetDataEdit.cid == null || !dataEditWidget || dataEditWidget.isInvalid) {
            displayErrors();
            return;
        }

        delete widgetDataEdit.id;

        props.glob.AppActions.widgetCreate(
            widgetEditWorkspace,
            widgetDataEdit.cid,
            widgetDataEdit.getDataForSave(),
            widgetParentWidget
        );

        if (widgetEditPos) {
            props.glob.AppActions.widgetAttach(
                widgetEditWorkspace,
                widgetDataEdit.cid,
                widgetEditPos[0],
                widgetEditPos[1],
                false,
                true,
                widgetParentWidget
            );
        } else {
            if (pos) {
                props.glob.AppActions.widgetAttach(
                    widgetEditWorkspace,
                    widgetDataEdit.cid,
                    pos[0],
                    pos[1],
                    false,
                    true,
                    widgetParentWidget
                );
            }
        }

        closeDialog();
    };

    const processOnSaveClick = (): boolean => {
        //LL: 02.06.20: sometimes isInvalid is not set, so call validate.
        //  For example, if some new validators are added and old props become invalid without any changes
        if (dataEditWidget != null && dataEditWidget.isInvalid != true) {
            dataEditWidget.validate();
        }

        if (widgetDataEdit.id == null || !dataEditWidget || dataEditWidget.isInvalid) {
            displayErrors();
            return false;
        }

        const data = widgetDataEdit.getDataForSave();

        if (data != null) {
            //EVG 11.05.2020:
            // When order of any propeties is changed during development this check always says that the saved widget has been changed even when it was not.
            // Saving the "changed" widget makes it correct.
            // Changing of properties' order is too hard to detect, so let it be as it is.
            if (initialData !== JSON.stringify(data)) {
                props.glob.AppActions.widgetSave(
                    widgetDataEdit.id,
                    data,
                    undefined,
                    undefined,
                    storeState.widgetEditWorkspace
                );
            }
        }

        return true;
    };

    const handleOnSaveAndCloseClick = () => {
        if (processOnSaveClick()) {
            closeDialog();
        }
    };

    const handleOnSetWidgetType = (id: number, type: string) => {
        props.glob.AppActions.configSetWidgetType(id, type);
    };

    const handleOnSetFieldValues = (id: number, type: string, values: IDataEditValues) => {
        props.glob.AppActions.configSetFieldValues(id, type, values);
    };

    const tryPaste = (jsonStr: string) => {
        try {
            const obj = JSON.parse(JSON.stringify(JSON.parse(jsonStr)));

            Utilities.jsonNormalize_DataSourceNameToID(obj, props.glob);
            Utilities.jsonNormalize_ThemeNameToID(obj, props.glob);

            const pastedWidgetData = WidgetData.getFromJSON(JSON.stringify(obj), props.glob, true).widgetData;

            pastedWidgetData.def.done(() => {
                pastedWidgetData.id = widgetDataEdit.id;

                if (widgetDataEdit.id == null) {
                    pastedWidgetData.cid = widgetDataEdit.cid;
                }

                const id = pastedWidgetData.id || pastedWidgetData.cid;

                handleOnSetWidgetType(id, pastedWidgetData.type);
                handleOnSetFieldValues(id, pastedWidgetData.type, pastedWidgetData.widget.values);
                props.glob.AppActions.configSetWidgetLayout(id, pastedWidgetData.layout);
            });
        } catch (e) {
            props.glob.AppActions.showNotification_Fail('Cannot paste widget data into workspace', null);
        }
    };

    const handleOnCreateTemplateClick = (e) => {
        if (
            (widgetDataEdit.cid == null && widgetDataEdit.id == null) ||
            dataEditWidget == null ||
            dataEditWidget.isInvalid
        ) {
            displayErrors();
            return;
        }

        const newCIDsWidgetData = WidgetData.getFromJSON(widgetDataEdit.getString(), props.glob, true).widgetData;

        props.glob.DialogActions.createDialog(
            'Select a folder and name for widget in collection',
            '',
            (dirid, name, overwrite) => {
                props.glob.AppActions.templateCreateSave(dirid, name, overwrite, newCIDsWidgetData.getString());
            },
            () => {},
            () => {},
            'templateDirSelect',
            {
                selectTitle: 'Save here',
                rootId: 2,
                nameSelect: true,
                name: widgetDataEdit.values[widgetDataEdit.type].name || 'New Widget',
                overwrite: false,
                overwriteShow: true,
                allowSelectItem: true
            }
        );
    };

    const handleOnCancelClick = () => {
        //EVG 24.04.2018: This has been done because I didn't find a way to clean brokenValidators collection accordingly to Taras' architecture.
        dropBrokenValidators();
        closeDialog();
    };

    const handleOnAddFromCollectionClick = () => {
        if (addColDef.current != null) {
            addColDef.current.reject();
        }

        const def = (addColDef.current = $.Deferred());

        props.glob.DialogActions.createDialog(
            'Select widget in collection',
            '',
            (id, data) => {
                def.resolve(data);
            },
            () => {},
            () => {},
            'templateDirSelect',
            {
                selectTitle: 'Add Selected Widget',
                rootId: 2,
                selectTemplateMode: true,
                overwriteShow: false,
                allowSelectItem: true
            }
        );

        def.done((data) => {
            tryPaste(JSON.stringify(data));
        });
    };

    const getDialogContainer = () => {
        return contentRef.current;
    };

    const handleOnEditJSONClick = (e) => {
        const data = JSON.parse(JSON.stringify(widgetDataEdit.getDataForSave()));

        Utilities.jsonNormalize_DataSourceIDToName(data, props.glob);
        Utilities.jsonNormalize_ThemeIDToName(data, props.glob);

        const text = JSON.stringify(data, null, '\t');

        props.glob.AppStore.saveToLocalStorage('pdc_clipboard_editdata', text);

        props.glob.DialogActions.createDialog(
            'Edit Widget Configuration as JSON',
            null,
            (obj) => {
                tryPaste(obj);
            },
            () => {},
            () => {},
            'custom',
            {
                deny: false,
                confirmTitle: 'Save',
                componentOptions: {
                    theme: props.theme,
                    dataSourceID: Utilities.getDataSourceID(text)
                },
                resizable: true,
                minHeight: 210,
                Component: EditTargetDialog
            }
        );
    };

    const handleOnPaste = (value: string) => {
        tryPaste(value);
    };

    const getSaveButtons = () => {
        if (widgetDataEdit == null || storeState.readOnly) {
            return;
        }

        const result: React.JSX.Element[] = [];

        if (widgetDataEdit.id == null) {
            result.push(
                <button
                    key="create"
                    className="btn btn-success"
                    onClick={() => {
                        createWidget(null);
                    }}
                >
                    {'Create'}
                </button>
            );
        } else {
            result.push(
                <button key="save-and-close" className="btn btn-success" onClick={handleOnSaveAndCloseClick}>
                    {'Save'}
                </button>
            );
        }

        if (widgetDataEdit.cid != null && dataEditWidget != null && !dataEditWidget.isInvalid) {
            result.push(
                <button key="save-in-collection" className="btn btn-default" onClick={handleOnCreateTemplateClick}>
                    {'Save in Collection'}
                </button>
            );
        }

        if (widgetDataEdit.id != null) {
            result.push(
                <button
                    key="save"
                    className="btn btn-default"
                    onClick={() => {
                        processOnSaveClick();
                    }}
                >
                    {'Apply'}
                </button>
            );
        }

        return result;
    };

    // const handleOnWindowResize = () => {
    //     if (resized) {
    //         return;
    //     }
    //     const headerHeight = $(headerRef.current).outerHeight();
    //     const newContainerSize = $(contentRef.current).height() - headerHeight;
    //     setContainerSize(newContainerSize);
    //     $('.listen-container-resize', contentRef.current).trigger('container-resize', [newContainerSize]);
    // };

    let content: React.JSX.Element;

    if (widgetDataEdit == null) {
        content = <pre>{'Incompatible JSON format'}</pre>;
    } else {
        content = (
            <>
                <div className="content-toolbar row w-100 d-flex flex-row">
                    <div className="flex-grow-1">
                        <div className="d-flex flex-row justify-content-start align-items-start">
                            <WidgetTypeSelect
                                id={widgetDataEdit.id}
                                cid={widgetDataEdit.cid}
                                type={widgetDataEdit.type}
                                getDialogContainer={getDialogContainer}
                                glob={props.glob}
                                //onUpdate={handleOnWindowResize}
                                onSetWidgetType={handleOnSetWidgetType}
                            />

                            <div
                                key="service-buttons"
                                className="btn-group ml-2"
                                role="group"
                                aria-label="Service Buttons"
                            >
                                <button className="btn btn-default" onClick={handleOnAddFromCollectionClick}>
                                    {'Select from Collection'}
                                </button>

                                <ClipboardCopyButton widgetDataEdit={widgetDataEdit} glob={props.glob} />

                                <PasteButton theme={props.theme} glob={props.glob} onPaste={handleOnPaste} />

                                <div className="btn btn-default" onClick={handleOnEditJSONClick}>
                                    <span className="glyphicon glyphicon-edit" />
                                    {'Edit JSON'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="main-content row w-100">
                    <WidgetParametersEdit
                        id={widgetDataEdit.id}
                        cid={widgetDataEdit.cid}
                        type={widgetDataEdit.type}
                        data={dataEditWidget}
                        loading={widgetDataEdit.loading}
                        showErrorsAsText={true}
                        theme={props.theme}
                        glob={props.glob}
                        //onUpdate={ handleOnWindowResize }
                        onSetFieldValues={handleOnSetFieldValues}
                    />
                </div>
            </>
        );
        //     <pre className="template-json">
        //         {
        //             state.widgetDataEdit.getPrettyString()
        //         }
        //     </pre>
    }

    const { modalStyle, containerStyle, contentStyle, bodyStyle } = getStyles(
        initialContainerHeight,
        initialContentWidth,
        containerSize.height,
        containerHeight,
        position,
        config.LAYOUT_TYPE === 'narrow'
    );

    return (
        <div className="template-edit widget-edit">
            <div className="modal fade show" style={modalStyle} role="dialog">
                <div className="modal-dialog" style={containerStyle}>
                    <div className="modal-content" style={contentStyle} ref={contentRef}>
                        <div className="modal-header" ref={headerRef}>
                            <h4 className="modal-title">{'Edit widget'}</h4>

                            <div className="btn-group" role="group" aria-label="Save Buttons">
                                {getSaveButtons()}

                                <button type="button" className="btn btn-default" onClick={handleOnCancelClick}>
                                    {'Cancel'}
                                </button>
                            </div>
                        </div>

                        <div className="modal-body container-fluid" style={bodyStyle}>
                            {content}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ClipboardCopyButton(props: { widgetDataEdit: WidgetData; glob: IGlob }) {
    const { widgetDataEdit } = props;

    if (
        widgetDataEdit.id == null ||
        widgetDataEdit.cid == null ||
        widgetDataEdit.widget == null ||
        widgetDataEdit.widget.isInvalid
    ) {
        return null;
    }

    const handleOnClipboardCopyClick = () => {
        const data = JSON.parse(JSON.stringify(widgetDataEdit.getDataForSave())); // Clone nested objects.

        Utilities.jsonNormalize_DataSourceIDToName(data, props.glob);
        Utilities.jsonNormalize_ThemeIDToName(data, props.glob);

        const text = JSON.stringify(data, null, '\t');

        props.glob.AppStore.saveToLocalStorage('pdc_clipboard_data', text);
    };

    const dataCopy = $.extend(true, {}, widgetDataEdit.getDataForSave());

    Utilities.jsonNormalize_DataSourceIDToName(dataCopy, props.glob);
    Utilities.jsonNormalize_ThemeIDToName(dataCopy, props.glob);

    return (
        <ClipboardCopy
            isButtonWithGlyph={false}
            text={JSON.stringify(dataCopy, null, '\t')}
            caption={'Copy to Clipboard'}
            onClick={handleOnClipboardCopyClick}
        />
    );
}
